// v1 placement-redesign: right-rail sidebar showing each recipient's
// color swatch + name + role + per-recipient field counts. Clicking a
// row makes that signer "active" — the next placement (via the
// AddFieldToolbar) attaches to them.
//
// Width: PDF_VIEWER.legendWidth. Visible only in configure mode.

import { type CSSProperties, useMemo } from 'react';
import { APP } from '../app/tokens';
import { PDF_VIEWER } from './pdfViewerTokens';
import { colorForSigner } from '../../lib/signerColors';
import type { ViewerSigner, FieldDescriptor } from './signingTypes';

export interface RecipientLegendProps {
  signers: ViewerSigner[];
  /** All fields currently in the working set, used to compute per-signer
   *  counts (total + confirmed). */
  fields: FieldDescriptor[];
  activeSignerIndex: number;
  onSelectSigner: (index: number) => void;
  /** Optional CTA when no signers — usually points back to wherever the
   *  user adds recipients. We render a link button if provided. */
  onAddRecipient?: () => void;
  /** Opens Send for Signature (invite flow). Always available when wired so users do not rely on chat chips alone. */
  onSendForSignature?: () => void;
  sendForSignatureDisabled?: boolean;
  /** Deprecated: recipient emails are collected in the send modal, not this legend. */
  onAddRecipientEmail?: (email: string) => void;
}

interface SignerCounts {
  total: number;
  confirmed: number;
}

export default function RecipientLegend({
  signers,
  fields,
  activeSignerIndex,
  onSelectSigner,
  onAddRecipient,
  onSendForSignature,
  sendForSignatureDisabled = false,
}: RecipientLegendProps) {
  // Pre-bin fields by signerIndex so each row is O(1) lookup.
  const counts = useMemo<Map<number, SignerCounts>>(() => {
    const map = new Map<number, SignerCounts>();
    for (const f of fields) {
      const idx = f.signerIndex ?? -1;
      if (idx < 0) continue;
      const cur = map.get(idx) ?? { total: 0, confirmed: 0 };
      cur.total += 1;
      if (f.state === 'confirmed' || f.state === 'signed') cur.confirmed += 1;
      map.set(idx, cur);
    }
    return map;
  }, [fields]);

  const sendFooter = onSendForSignature ? (
    <div style={FOOTER}>
      <button
        type="button"
        onClick={onSendForSignature}
        disabled={sendForSignatureDisabled}
        style={{
          ...SEND_BTN,
          ...(sendForSignatureDisabled ? SEND_BTN_DISABLED : {}),
        }}
        title={
          sendForSignatureDisabled
            ? 'Save or generate a PDF first so we know which file to send.'
            : 'Invite others to sign this document'
        }
      >
        Send for signature
      </button>
    </div>
  ) : null;

  if (signers.length === 0) {
    return (
      <aside style={WRAPPER}>
        <div style={HEADER}>Signers</div>
        <div style={{ ...EMPTY, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: APP.textMuted, lineHeight: 1.4 }}>
            No recipients yet. Add at least one to start placing signature fields.
          </div>
          {onAddRecipient && (
            <button type="button" onClick={onAddRecipient} style={CTA_BTN}>
              Add recipient
            </button>
          )}
        </div>
        {sendFooter}
      </aside>
    );
  }

  const selfEntries = signers
    .map((s, i) => ({ signer: s, index: i }))
    .filter(({ signer }) => signer.role === 'self');
  const otherEntries = signers
    .map((s, i) => ({ signer: s, index: i }))
    .filter(({ signer }) => signer.role !== 'self');

  /** Fallback if a caller omits `role` — treat first row as self, rest as others. */
  const selfRows = selfEntries.length > 0
    ? selfEntries
    : [{ signer: signers[0], index: 0 }];
  const otherRows = selfEntries.length > 0
    ? otherEntries
    : signers.slice(1).map((signer, j) => ({ signer, index: j + 1 }));

  const renderSignerRow = (signer: ViewerSigner, i: number) => {
    const color = colorForSigner(i);
    const c = counts.get(i) ?? { total: 0, confirmed: 0 };
    const active = i === activeSignerIndex;
    return (
      <button
        key={`${signer.email}-${i}`}
        type="button"
        onClick={() => onSelectSigner(i)}
        style={{
          ...ROW,
          borderLeftColor: active ? color.outline : 'transparent',
          backgroundColor: active ? `${color.outline}10` : 'transparent',
          fontWeight: active ? 600 : 500,
        }}
        aria-pressed={active}
        title={`Make ${signer.name || signer.email} the active signer for new placements`}
      >
        <span
          aria-hidden
          style={{
            width: PDF_VIEWER.swatchSize,
            height: PDF_VIEWER.swatchSize,
            borderRadius: PDF_VIEWER.swatchSize / 2,
            backgroundColor: color.outline,
            flexShrink: 0,
            marginTop: 4,
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span style={NAME}>{signer.name || signer.email}</span>
          <span style={ROLE_LINE}>{signer.role === 'self' ? 'You' : signer.role === 'other' ? 'Recipient' : signer.role || '—'}</span>
          <span style={COUNT_LINE}>
            {c.total === 0
              ? 'No fields'
              : `${c.total} field${c.total === 1 ? '' : 's'}${c.confirmed > 0 ? ` · ${c.confirmed} ✓` : ''}`}
          </span>
        </div>
      </button>
    );
  };

  return (
    <aside style={WRAPPER}>
      <div style={HEADER}>Signers</div>
      <div style={LIST_SCROLL}>
        <div style={SUBHEADER}>You</div>
        {selfRows.map(({ signer, index }) => renderSignerRow(signer, index))}

        <div style={{ ...SUBHEADER, marginTop: 8 }}>Recipients</div>
        {otherRows.length === 0 ? (
          <div style={RECIPIENTS_EMPTY}>
            <span style={{ fontSize: 11, color: APP.textMuted, lineHeight: 1.45 }}>
              No recipient row is available for this document yet.
            </span>
            {onAddRecipient && (
              <button type="button" onClick={onAddRecipient} style={CTA_BTN}>
                Add recipient
              </button>
            )}
          </div>
        ) : (
          otherRows.map(({ signer, index }) => renderSignerRow(signer, index))
        )}
      </div>
      {sendFooter}
    </aside>
  );
}

const WRAPPER: CSSProperties = {
  width: PDF_VIEWER.legendWidth,
  flexShrink: 0,
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderLeft: `1px solid ${APP.border}`,
  backgroundColor: APP.bgContent,
  fontFamily: APP.fontFamily,
  overflow: 'hidden',
};

const HEADER: CSSProperties = {
  padding: '12px 14px 8px',
  fontSize: 11,
  fontWeight: 600,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  borderBottom: `1px solid ${APP.border}`,
};

const SUBHEADER: CSSProperties = {
  padding: '8px 10px 4px',
  fontSize: 10,
  fontWeight: 600,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.35,
};

const RECIPIENTS_EMPTY: CSSProperties = {
  padding: '4px 10px 10px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const LIST_SCROLL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: 6,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

const ROW: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  padding: '8px 10px',
  borderLeft: '3px solid transparent',
  background: 'transparent',
  border: 0,
  borderLeftStyle: 'solid',
  borderLeftWidth: 3,
  textAlign: 'left',
  cursor: 'pointer',
  borderRadius: 4,
  transition: 'background-color 120ms',
  fontFamily: 'inherit',
};

const NAME: CSSProperties = {
  fontSize: 12,
  color: APP.textPrimary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const ROLE_LINE: CSSProperties = {
  fontSize: 10,
  color: APP.textMuted,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const COUNT_LINE: CSSProperties = {
  fontSize: 10,
  color: APP.textMuted,
  marginTop: 2,
};

const EMPTY: CSSProperties = {
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const CTA_BTN: CSSProperties = {
  alignSelf: 'flex-start',
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid ${APP.border}`,
  background: 'transparent',
  color: APP.textPrimary,
  fontSize: 12,
  fontFamily: APP.fontFamily,
  cursor: 'pointer',
};

const FOOTER: CSSProperties = {
  flexShrink: 0,
  padding: '10px 12px 12px',
  borderTop: `1px solid ${APP.border}`,
};

const SEND_BTN: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: PDF_VIEWER.fieldSigned,
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: APP.fontFamily,
  cursor: 'pointer',
};

const SEND_BTN_DISABLED: CSSProperties = {
  backgroundColor: APP.border,
  color: APP.textMuted,
  cursor: 'not-allowed',
  opacity: 0.88,
};
